import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    DO_NOTHING,
    FlyFreelyError,
    FlyFreelyLoggingService,
    LocationFeatureDto,
    Site,
    SiteService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { FeatureGroup } from '@flyfreely-portal-ui/map';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { SiteDialoguesService } from '../site-dialogues.service';

@Component({
    selector: 'site-list',
    templateUrl: './site-list.component.html'
})
export class SiteList implements OnInit, OnDestroy {
    @Input() organisationId: number;
    @Input() managingOrganisationId: number;
    @Input() canEdit = false;

    sites: Site[];
    showArchived = false;

    private workTracker = new WorkTracker();
    working = false;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private siteService: SiteService,
        private commonDialoguesService: CommonDialoguesService,
        private siteDialoguesService: SiteDialoguesService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.refreshSites();
        this.siteService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshSites());
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshSites() {
        this.siteService
            .findSites(this.organisationId, this.managingOrganisationId, true)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: sites => {
                    this.sites = [...sites];
                    this.sites.sort((a, b) => {
                        const aIsInOrg =
                            a.organisation.id === this.organisationId;
                        const bIsInOrg =
                            b.organisation.id === this.organisationId;

                        if (aIsInOrg !== bIsInOrg) {
                            return aIsInOrg ? -1 : 1;
                        }

                        return a.name.localeCompare(b.name);
                    });
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error refreshing site list: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    newSite() {
        this.siteDialoguesService.showSiteEdit(
            this.organisationId,
            this.managingOrganisationId,
            null
        );
    }

    editSite(site: Site) {
        this.siteDialoguesService.showSiteEdit(
            this.organisationId,
            this.managingOrganisationId,
            { ...site }
        );
    }

    archiveSite(site: Site) {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Archive Site',
                `Are you sure you want to archive "${site.name}"?`,
                'Archive',
                () =>
                    firstValueFrom(
                        this.siteService
                            .archiveSite(site.id, this.managingOrganisationId)
                            .pipe(
                                tap({
                                    error: (error: FlyFreelyError) =>
                                        this.logging.error(
                                            error,
                                            `Error archiving site: ${error.message}`
                                        )
                                })
                            )
                    )
            )
            .then(DO_NOTHING, DO_NOTHING)
            .finally(() => doneWorking());
    }

    unarchiveSite(site: Site) {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Unarchive Site',
                `Are you sure you want to unarchive "${site.name}"?`,
                'Unarchive',
                () =>
                    firstValueFrom(
                        this.siteService
                            .unarchiveSite(site.id, this.managingOrganisationId)
                            .pipe(
                                tap({
                                    error: (error: FlyFreelyError) =>
                                        this.logging.error(
                                            error,
                                            `Error unarchiving site: ${error.message}`
                                        )
                                })
                            )
                    )
            )
            .then(DO_NOTHING, DO_NOTHING)
            .finally(() => doneWorking());
    }

    editSiteAreas(site: Site) {
        const existingFeatures = (site.siteAreaList ?? [])
            .filter(area => area.boundary != null)
            .map((area, index) => ({
                id: index,
                name: area.name,
                geom: area.boundary as GeoJSON.Polygon
            }));

        const defaultFeatures: FeatureGroup[] = [
            {
                id: 0,
                name: 'Area',
                type: 'Polygon',
                existingFeatures,
                categories: [
                    {
                        id: LocationFeatureDto.Type.AREA_OF_INTEREST,
                        name: 'Site Area'
                    }
                ]
            } as FeatureGroup
        ];

        const dialogue = this.siteDialoguesService.showSiteLocationEdit(
            this.organisationId,
            site.name,
            site.siteAreaList
        );

        dialogue
            .content!.siteAreasChange.pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(siteAreaList => {
                this.siteService
                    .updateSiteAreas({
                        siteId: site.id,
                        managingOrganisationId: this.managingOrganisationId,
                        siteAreaList
                    })
                    .subscribe({
                        next: () => this.logging.success('Site areas updated'),
                        error: (error: FlyFreelyError) =>
                            this.logging.error(
                                error,
                                `Error updating site areas: ${error.message}`
                            )
                    })
                    .add(this.workTracker.createTracker());
            });
    }
}
